"use client";

import useEmblaCarousel from "embla-carousel-react";
import { useCallback } from "react";
import Image from "next/image";
import type { CustomerReview } from "./data";
import { ArrowLeft, ArrowRight } from "@/components/core/icons/Arrows";

export default function CustomerReviewsSlider ({
  testimonials,
}: {
  testimonials: CustomerReview[];
}) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "center",
    // 2-up left-aligned at lg, 3-up centered at xl.
    // Later matching breakpoints override earlier ones.
    breakpoints: {
      "(min-width: 1024px)": { align: "start" },
      "(min-width: 1280px)": { align: "center" },
    },
  });

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const items = testimonials;

  return (
    <div className="relative">
      <div className="overflow-hidden" ref={emblaRef}>
        {/* Spacing lives on each slide (padding + negative container margin)
            rather than flex `gap`, so the gap is preserved across the loop
            wrap-around between the last and first slide. */}
        <div className="flex -ml-4 lg:-ml-6">
          {items.map((testimonial, index) => (
            <div
              key={`${testimonial.id}-${index}`}
              className="shrink-0 w-full lg:w-1/2 xl:w-1/3 pl-4 lg:pl-6"
            >
              <div className="bg-transparent border border-grey-850 rounded-lg p-6 h-full flex flex-col">
                <div className="flex items-start gap-3 mb-6">
                  {testimonial.author?.image && (
                    <div className="relative size-10 shrink-0 rounded-full overflow-hidden">
                      <Image
                        src={testimonial.author.image.src}
                        alt={testimonial.author.name}
                        fill
                        className="object-cover rounded-full"
                      />
                    </div>
                  )}
                  <div>
                    <h3 className="font-medium text-base m-0 text-white">
                      {testimonial.author?.name}
                    </h3>
                    <p className=" text-white text-base m-0">
                      {testimonial.author?.role}
                    </p>
                  </div>
                </div>
                <p className="text-grey-550 leading-xxl line-clamp-5 text-[15px]">
                  {testimonial.quote}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-center items-center gap-4 mt-8 lg:contents">
        <button title="Previous Testimonial" onClick={scrollPrev} className="[&>svg]:w-6 lg:absolute lg:top-1/2 lg:-translate-y-1/2 lg:left-0 lg:-translate-x-16 xl:left-[-24px] min-w-[54px] min-h-[54px] rounded-full bg-grey-900 flex items-center justify-center hover:bg-grey-800 transition-colors">
          <ArrowLeft />
        </button>
        <button title="Next Testimonial" onClick={scrollNext} className="[&>svg]:w-6 lg:absolute lg:top-1/2 lg:-translate-y-1/2 lg:right-0 lg:translate-x-16 xl:right-[-24px] min-w-[54px] min-h-[54px] rounded-full bg-grey-900 flex items-center justify-center hover:bg-grey-800 transition-colors">
          <ArrowRight />
        </button>
      </div>
    </div>
  );
};
